import type { Tour } from "./types";

/**
 * Atlas tours — governance + settings. Atlas is the cross-platform
 * inventory + compliance surface; the tours articulate how it makes a
 * sprawling BI estate auditable in one view.
 */

const NAV = (path: string) => `nav[data-tour-id="nav"] a[href="${path}"]`;

export const governanceTour: Tour = {
  id: "atlas.governance.v1",
  title: "Welcome to Atlas",
  subtitle: "Cross-platform BI governance + compliance.",
  steps: [
    {
      placement: "center",
      title: "One view across every BI platform",
      body:
        "Atlas inventories Cognos, Power BI, Tableau, and Qlik content in one normalized graph — owners, lineage, last-run timestamps, permissions. Compliance teams stop chasing spreadsheets; engineers stop reverse-engineering ownership.",
    },
    {
      target: '[data-tour-id="governance-rules"]',
      placement: "right",
      title: "Governance rules",
      body:
        "Rules express what \"good\" looks like — \"every dashboard must have a current owner\", \"no PII in dashboards published to the public workspace\", \"reports unused for 12 months are stale\". Atlas evaluates rules continuously and flags violations.",
    },
    {
      target: '[data-tour-id="governance-inventory"]',
      placement: "bottom",
      title: "Inventory — the foundation",
      body:
        "Atlas reads from each platform's inventory (Cognos content store + audit DB; Power BI / Tableau / Qlik when their replicators land). The inventory panel shows when each platform was last refreshed and surfaces ingestion errors before they become governance gaps.",
    },
    {
      target: '[data-tour-id="governance-violations"]',
      placement: "top",
      title: "Violations triage",
      body:
        "Each violation carries the platform, content path, severity, and the specific rule it tripped. Filter by platform, severity, owner; export to CSV; or hand off to Curate as a cleanup plan in one click.",
    },
    {
      placement: "center",
      title: "Always replayable",
      body:
        "Every governance check is timestamped and auditable. The help icon (?) replays this tour anytime.",
    },
  ],
};

export const settingsTour: Tour = {
  id: "atlas.settings.v1",
  title: "Settings",
  subtitle: "Provider, license, rule inventory.",
  steps: [
    {
      placement: "center",
      title: "What Atlas believes about itself",
      body:
        "Settings shows the LLM provider (used to write violation rationales), the license state, and the count of governance rules currently loaded. All knobs are env-bound; this view is read-only.",
    },
    {
      target: '[data-tour-id="settings-llm"]',
      placement: "right",
      title: "LLM provider",
      body:
        "Atlas uses the LLM to render plain-English rationales on each violation — \"why is this report flagged?\". With no provider configured, Atlas falls back to the rule's own description text.",
    },
    {
      target: '[data-tour-id="settings-rules"]',
      placement: "right",
      title: "Rule inventory",
      body:
        "The total rule count surfaces at the top of every page. Rules are loaded from the bundle's resources at startup; adding a new rule means rebuilding and redeploying.",
    },
  ],
};

export function tourForPath(pathname: string): Tour | undefined {
  if (pathname.startsWith("/governance")) return governanceTour;
  if (pathname.startsWith("/settings")) return settingsTour;
  return undefined;
}
